import React from 'react';

import {
    Ear,
    Handshake,
    Heart,
    Lightbulb,
    Search,
    Sparkles,
} from 'lucide-react';

import { IMSARMBTSReport, IMSARMBTSRubricParameter } from '../../../../types/services.interfaces/mbts-ux';

import { ConsentLock } from '../ConsentLock';

import { SectionTooltip } from './SectionTooltip';
import { BENCHMARK_PCT, SECTION_IDS, getRubricLevel } from './utils';

interface CognitiveMasteryProps {
    report: IMSARMBTSReport;
    isConsentLocked: boolean;
    openConsentDataGather: () => void;
};

const skillIconFor = (title: string) => {
    const key = title.toLowerCase();
    if (key.includes('empathy') || key.includes('emotion')) return Heart;
    if (key.includes('curios') || key.includes('inquir')) return Lightbulb;
    if (key.includes('align') || key.includes('collab')) return Handshake;
    if (key.includes('listen')) return Ear;
    if (key.includes('sense') || key.includes('analy')) return Search;
    return Sparkles;
};

const RadarChart = ({ parameters }: { parameters: IMSARMBTSRubricParameter[] }) => {
    const size = 280;
    const center = size / 2;
    const radius = 95;
    const levels = 4;
    const count = parameters.length || 1;

    const pointFor = (index: number, scorePct: number) => {
        const angle = ((Math.PI * 2) / count) * index - Math.PI / 2;
        const r = radius * (scorePct / 100);
        return {
            x: center + r * Math.cos(angle),
            y: center + r * Math.sin(angle),
        };
    };

    const polygonPoints = (scores: number[]) =>
        scores.map((score, index) => {
            const { x, y } = pointFor(index, score);
            return `${x},${y}`;
        }).join(' ');

    const currentScores = parameters.map(p => p.score);
    const benchmarkScores = parameters.map(() => BENCHMARK_PCT);

    return (
        <svg viewBox={`0 0 ${size} ${size}`} className="mbts_analysis_v2__mastery__radar">
            {Array.from({ length: levels }).map((_, levelIdx) => {
                const levelRadius = (radius / levels) * (levelIdx + 1);
                const points = Array.from({ length: count }).map((__, i) => {
                    const angle = ((Math.PI * 2) / count) * i - Math.PI / 2;
                    return `${center + levelRadius * Math.cos(angle)},${center + levelRadius * Math.sin(angle)}`;
                }).join(' ');
                return (
                    <polygon
                        key={`grid-${levelIdx}`}
                        points={points}
                        className="mbts_analysis_v2__mastery__radar__grid"
                    />
                );
            })}

            {parameters.map((parameter, index) => {
                const angle = ((Math.PI * 2) / count) * index - Math.PI / 2;
                const tipX = center + radius * Math.cos(angle);
                const tipY = center + radius * Math.sin(angle);
                const labelX = center + (radius + 22) * Math.cos(angle);
                const labelY = center + (radius + 18) * Math.sin(angle);

                return (
                    <g key={parameter.testing_skill.title}>
                        <line
                            x1={center}
                            y1={center}
                            x2={tipX}
                            y2={tipY}
                            className="mbts_analysis_v2__mastery__radar__axis"
                        />
                        <text
                            x={labelX}
                            y={labelY}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            className="mbts_analysis_v2__mastery__radar__label"
                        >
                            {parameter.testing_skill.short_title || parameter.testing_skill.title}
                        </text>
                    </g>
                );
            })}

            <polygon
                points={polygonPoints(benchmarkScores)}
                className="mbts_analysis_v2__mastery__radar__benchmark"
            />
            <polygon
                points={polygonPoints(currentScores)}
                className="mbts_analysis_v2__mastery__radar__current"
            />

            {currentScores.map((score, index) => {
                const { x, y } = pointFor(index, score);
                return (
                    <circle
                        key={`dot-${index}`}
                        cx={x}
                        cy={y}
                        r={4}
                        className="mbts_analysis_v2__mastery__radar__dot"
                    />
                );
            })}
        </svg>
    );
};

export const CognitiveMastery = (props: CognitiveMasteryProps) => {
    const { report, isConsentLocked, openConsentDataGather } = props;

    return (
        <section id={SECTION_IDS.mastery} className="mbts_analysis_v2__section mbts_analysis_v2__mastery">
            <ConsentLock isConsentLocked={isConsentLocked} openConsentDataGather={openConsentDataGather} />

            <div className="mbts_analysis_v2__section__head">
                <div>
                    <div className="mbts_analysis_v2__section__title_row">
                        <span className="mbts_analysis_v2__section__dot mbts_analysis_v2__section__dot--brand" />
                        <h3 className="mbts_analysis_v2__section__title">Cognitive Mastery Breakdown</h3>
                    </div>
                    <p className="mbts_analysis_v2__section__subtitle">
                        Comparing your active skills against the{' '}
                        <span>
                            {report.player_milestone_role} ({report.player_milestone_level}) Benchmark ({(BENCHMARK_PCT / 20).toFixed(1)}/5.0)
                        </span>
                    </p>
                </div>
                <div className="mbts_analysis_v2__mastery__legend">
                    <span className="mbts_analysis_v2__mastery__legend__item">
                        <span className="mbts_analysis_v2__mastery__legend__swatch mbts_analysis_v2__mastery__legend__swatch--score" />
                        Your Score
                    </span>
                    <span className="mbts_analysis_v2__mastery__legend__item">
                        <span className="mbts_analysis_v2__mastery__legend__swatch mbts_analysis_v2__mastery__legend__swatch--target" />
                        Target ({BENCHMARK_PCT}%)
                    </span>
                </div>
            </div>

            <div className="mbts_analysis_v2__mastery__layout">
                <div className="mbts_analysis_v2__mastery__chart_panel">
                    <RadarChart parameters={report.rubric_parameters} />
                    <p className="mbts_analysis_v2__mastery__chart_hint">
                        Skill profile vs {report.player_milestone_level} benchmark
                    </p>
                </div>

                <div className="mbts_analysis_v2__mastery__skills">
                    {report.rubric_parameters.map(parameter => {
                        const level = getRubricLevel(parameter.score);
                        const Icon = skillIconFor(parameter.testing_skill.title);

                        return (
                            <div
                                key={parameter.testing_skill.title}
                                className={`mbts_analysis_v2__mastery__skill mbts_analysis_v2__mastery__skill--${level.level}`}
                            >
                                <div className="mbts_analysis_v2__mastery__skill__top">
                                    <span className="mbts_analysis_v2__mastery__skill__name">
                                        <Icon />
                                        <span>{parameter.testing_skill.title}</span>
                                        <SectionTooltip content={parameter.testing_skill.tooltip_content ?? ''} />
                                    </span>
                                    <span className="mbts_analysis_v2__mastery__skill__score">
                                        {parameter.score}%
                                        <span> (Target: {BENCHMARK_PCT}%)</span>
                                    </span>
                                </div>
                                <div className="mbts_analysis_v2__mastery__skill__track">
                                    <div
                                        className="mbts_analysis_v2__mastery__skill__fill"
                                        style={{ width: `${Math.min(parameter.score, 100)}%` }}
                                    />
                                    <div
                                        className="mbts_analysis_v2__mastery__skill__target"
                                        style={{ left: `${BENCHMARK_PCT}%` }}
                                    />
                                </div>
                                <p className="mbts_analysis_v2__mastery__skill__gap">
                                    {parameter.recommendation}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
